'use strict';

const moment = require('moment');
const { redirectToLDPWhenMediaReviewNotAllowed } = require('./redirects.js');
const { canAccessLabelsDevUi } = require('../../../modules/locationLabels/labelsFeatureFlagged.mts');
const service = require('../../../shared/services/pmtAccessService.js');
const {
	googleKeywordsInsightsArgsResolver,
	googleInsightsArgsResolver,
	overviewInsightsArgsResolver,
	ratingInsightsArgsResolver
} = require('../resolvers/insightsArgsResolvers.js');
const { onDateRangeChanged } = require('../subscriptions/insightsSubscriptions.js');
const { onLocationsListViewTypeChanged } = require('../subscriptions/locationSubscriptions.js');

const beforeUnloadListener = event => {
	event.preventDefault();
	event.returnValue = '';
};

const navigationState = { shouldPrevent: false };

const locationListArgsResolver = async ($stateParams, pmtFilterService, auth) => {
	let hasCreateLocationPermission = false;
	service
		.checkPermission(auth.user.selectedSite.name, auth.user, 'permission/location.view')
		.then(x => (hasCreateLocationPermission = x));

	const user = await auth.getUser($stateParams.site);
	const { places = {}, analytics, socialMedia } = user?.selectedSite ?? {};
	const { google = {}, apple = {}, facebook, bing, factual } = places;

	return {
		initialFilter: await pmtFilterService.parseFilter($stateParams),
		query: $stateParams.query,
		openFilterOnLoad: $stateParams.openFilterOnLoad === 'true',
		hasCreateLocationPermission,
		viewType: user?.settings?.locationListView || 'list',
		analytics,
		siteCategories: {
			google: {
				primary: google.categories?.primaryCategory,
				additional: google.categories?.additionalCategories
			},
			facebook,
			bing,
			apple: {
				primary: apple.categories?.primaryCategory,
				additional: apple.categories?.additionalCategories
			},
			factual
		},
		postEnabled: socialMedia?.locationPost.enabled
	};
};

const locationInsightStates = ({ elm, elmWithFilter }) => {
	return [
		{
			name: 'site.Locations.Location.Insights',
			url: '/insights',
			redirectTo: 'site.Locations.Location.Insights.Overview',
			requiredPermission: 'permission/location.view'
		},
		elm({
			name: 'site.Locations.Location.Insights.Overview',
			moduleName: 'Insights.Overview.Main',
			argsResolver: [
				'pmtEnvService',
				'$stateParams',
				'pmtFilterService',
				'auth',
				async (pmtEnvService, $stateParams, pmtFilterService, auth) => {
					const overviewInsightsArgs = await overviewInsightsArgsResolver(
						pmtEnvService,
						$stateParams,
						pmtFilterService,
						auth
					);
					return {
						...overviewInsightsArgs,
						placeId: $stateParams.placeId
					};
				}
			],
			url: '/overview?from&to',
			requiredPermission: 'role/user',
			pageTitle: 'general.insights.page_title'
		}),
		elmWithFilter({
			name: 'site.Locations.Location.Insights.Rating',
			moduleName: 'Insights.Rating.Main',
			url: '/rating?filter&from&to',
			requiredPermission: 'permission/location.view',
			argsResolver: [
				'pmtEnvService',
				'$stateParams',
				'pmtFilterService',
				'auth',
				ratingInsightsArgsResolver
			],
			subscriptions: ['$state', $state => [onDateRangeChanged($state)]]
		}),
		elm({
			name: 'site.Locations.Location.Insights.Keywords',
			moduleName: 'Insights.Keywords.Main',
			argsResolver: ['pmtEnvService', '$stateParams', googleKeywordsInsightsArgsResolver],
			url: '/keywords?from&to',
			requiredPermission: 'role/user',
			pageTitle: 'general.insights.page_title'
		}),
		elm({
			name: 'site.Locations.Location.Insights.Google',
			moduleName: 'Insights.Google.Main',
			argsResolver: [
				'pmtEnvService',
				'$stateParams',
				'pmtFilterService',
				async (pmtEnvService, $stateParams, pmtFilterService) => {
					const googleInsightsArgs = await googleInsightsArgsResolver(
						pmtEnvService,
						$stateParams,
						pmtFilterService
					);
					return {
						...googleInsightsArgs,
						placeId: $stateParams.placeId
					};
				}
			],
			url: '/google?from&to',
			requiredPermission: 'role/user',
			pageTitle: 'general.insights.page_title'
		}),
		elm({
			name: 'site.Locations.Location.Insights.Apple',
			moduleName: 'Insights.Apple.Main',
			argsResolver: [
				'pmtEnvService',
				'$stateParams',
				'auth',
				(pmtEnvService, $stateParams, auth) => {
					return {
						siteName: $stateParams.site || $stateParams.siteName,
						today: new Date().toISOString(),
						insightsUrl: pmtEnvService.insightsApiUrl,
						baseUrl: pmtEnvService.listingsManagerUrl,
						placeId: $stateParams.placeId,
						timezoneOffset: new Date().getTimezoneOffset() * -1,
						startOfMonth: moment().utc().startOf('month').valueOf(),
						appleConnectionType:
							auth.user?.selectedSite?.analytics?.apple?.connectionType || 'system'
					};
				}
			],
			url: '/apple?from&to',
			requiredPermission: 'role/user',
			pageTitle: 'general.insights.page_title'
		}),
		elm({
			name: 'site.Locations.Location.Insights.Bing',
			moduleName: 'Insights.Bing.Main',
			argsResolver: [
				'pmtEnvService',
				'$stateParams',
				(pmtEnvService, $stateParams) => {
					return {
						siteName: $stateParams.site || $stateParams.siteName,
						today: new Date().toISOString(),
						insightsUrl: pmtEnvService.insightsApiUrl,
						baseUrl: pmtEnvService.listingsManagerUrl,
						placeId: $stateParams.placeId,
						timezoneOffset: new Date().getTimezoneOffset() * -1,
						startOfMonth: moment().utc().startOf('month').valueOf()
					};
				}
			],
			url: '/bing?from&to',
			requiredPermission: 'role/user',
			pageTitle: 'general.insights.page_title'
		}),
		elm({
			name: 'site.Locations.Location.Insights.Facebook',
			moduleName: 'Insights.Facebook.Main',
			argsResolver: [
				'pmtEnvService',
				'$stateParams',
				(pmtEnvService, $stateParams) => {
					return {
						siteName: $stateParams.site || $stateParams.siteName,
						today: new Date().toISOString(),
						insightsUrl: pmtEnvService.insightsApiUrl,
						baseUrl: pmtEnvService.listingsManagerUrl,
						placeId: $stateParams.placeId,
						timezoneOffset: new Date().getTimezoneOffset() * -1,
						startOfMonth: moment().utc().startOf('month').valueOf()
					};
				}
			],
			url: '/facebook?from&to',
			requiredPermission: 'role/user',
			pageTitle: 'general.insights.page_title'
		})
	];
};

const locationDetailsStates = () => {
	return [
		{
			name: 'site.Locations.Location',
			url: '/:placeId',
			component: 'pmtLocation',
			reloadOnSearch: false,
			redirectTo: 'site.Locations.Location.Details',
			requiredPermission: 'permission/location.view'
		},
		{
			name: 'site.Locations.Location.Details',
			url: '/details',
			component: 'pmtLocationDetailsElm',
			reloadOnSearch: false,
			requiredPermission: 'permission/location.view'
		},
		{
			name: 'site.Locations.Location.NetworkStatus',
			url: '/network-status',
			component: 'pmtLocationNetworkStatus',
			reloadOnSearch: false,
			requiredPermission: 'permission/location.view'
		},
		{
			name: 'site.Locations.Location.MediaReview',
			url: '/media-review',
			component: 'pmtLocationMediaReviewElm',
			reloadOnSearch: false,
			requiredPermission: 'permission/location.view',
			redirectTo: redirectToLDPWhenMediaReviewNotAllowed
		},
		{
			name: 'site.Locations.Location.Audit',
			url: '/audit',
			templateUrl: 'components/location/audit/auditView.html',
			controller: 'AuditCtrl',
			requiredPermission: 'permission/location.view'
		},
		{
			name: 'site.Locations.Location.Conflicts',
			url: '/conflicts',
			component: 'pmtConflictResolver',
			requiredPermission: 'permission/task.conflict.manage'
		}
	];
};

const locationStates = ({ elm, elmWithFilter }) => {
	return [
		{
			name: 'site.Locations.Import',
			url: '/import',
			templateUrl: 'components/import/import.html',
			controller: 'ImportCtrl',
			requiredPermission: 'permission/location.edit'
		},
		{
			name: 'site.Locations.SpecialHours',
			url: '/specialhours',
			templateUrl: 'components/specialhours/specialhours.html',
			controller: 'SpecialHoursCtrl',
			requiredPermission: 'permission/location.edit'
		},
		elm({
			name: 'site.Locations.TemporarilyClosed',
			moduleName: 'Locations.UpdateMultiple.TemporarilyClosed',
			url: '/temporarilyclosed',
			requiredPermission: 'permission/location.edit',
			pageTitle: 'listings.navigation.temporarily_closed',
			argsResolver: [
				'$stateParams',
				$stateParams => {
					return {
						siteName: $stateParams.site || $stateParams.siteName,
						today: new Date().toISOString()
					};
				}
			]
		}),
		elm({
			name: 'site.Locations.Spreadsheet',
			moduleName: 'Locations.Spreadsheet.Main',
			url: '/spreadsheet',
			requiredPermission: 'permission/location.edit',
			pageTitle: 'listings.navigation.spreadsheet',
			argsResolver: [
				'$stateParams',
				$stateParams => ({ siteName: $stateParams.site || $stateParams.siteName })
			]
		}),
		elm({
			name: 'site.Locations.BookingLinks',
			moduleName: 'Locations.BookingLinks.Main',
			url: '/bookingLinks',
			requiredPermission: 'role/pinmetodeveloper',
			argsResolver: [
				'$stateParams',
				$stateParams => ({ siteName: $stateParams.site || $stateParams.siteName })
			]
		}),
		elm({
			name: 'site.Locations.Labels',
			moduleName: 'Locations.Labels.Main',
			url: '/labels',
			requiredPermission: 'permission/location.view',
			pageTitle: 'Labels (dev)',
			argsResolver: [
				'$stateParams',
				'auth',
				($stateParams, auth) =>
					auth.getUser($stateParams.site).then(user => ({
						isLabelsFeatureFlaggedUser: canAccessLabelsDevUi({
							email: user.email,
							isDemo: user.isDemo,
							isOwner: user.localAccess.roles.has('owner')
						})
					}))
			]
		}),
		{
			name: 'site.Locations',
			url: '/locations',
			templateUrl: 'components/location/locationList/pmtLocationsMenu.html',
			abstract: true
		},
		elmWithFilter({
			name: 'site.Locations.List',
			moduleName: 'Locations.List.Main',
			url: '?view&query&f&filter&openFilterOnLoad',
			argsResolver: ['$stateParams', 'pmtFilterService', 'auth', locationListArgsResolver],
			pageTitle: 'general.locations',
			requiredPermission: 'permission/location.view',
			subscriptions: [
				'auth',
				'$stateParams',
				(auth, $stateParams) => [onLocationsListViewTypeChanged(auth, $stateParams.site)]
			]
		}),
		{
			name: 'site.Locations.ListOld',
			url: '/list',
			redirectTo: 'site.Locations.List'
		},
		// Start add new location elm page
		elm({
			name: 'site.Locations.AddLocationNew',
			moduleName: 'Locations.AddLocation.Main',
			url: '/add',
			argsResolver: [
				'$stateParams',
				'auth',
				async ($stateParams, auth) => {
					return auth.getUser($stateParams.site).then(user => {
						const featureFlags = {
							places: {
								...user.selectedSite?.places.feature,
								allowAppleCustomName: user.selectedSite?.places?.feature?.appleCustomStoreNames // We need to map this, because they dont align for some reason
							}
						};

						return {
							analytics: user.selectedSite?.analytics,
							featureFlags,
							displayName: user.selectedSite?.displayName || '',
							chainName: user.selectedSite?.chainName || ''
						};
					});
				}
			],
			subscriptions: [
				'$window',
				'$rootScope',
				'$i18next',
				($window, $rootScope, $i18next) => [
					{
						id: 'PreventOutNavigation',
						f: shouldPrevent => {
							navigationState.shouldPrevent = shouldPrevent;

							// Prevent reloading, e.g. refreshing windows
							if (shouldPrevent) {
								$window.addEventListener('beforeunload', beforeUnloadListener);
							} else {
								$window.removeEventListener('beforeunload', beforeUnloadListener);
							}

							// Prevent navigation by navigation, e.g. clicking links
							// This is a minor hack to prevent registering new listeners, since we have no ability to unregister them due to how
							// the elm service deals (or rather doesn't deal with lifetimes) of subscriptions
							if (
								!$rootScope.$$listeners.$locationChangeStart.some(l => l.isLocationChangerListener)
							) {
								const navigationListener = function (e, next, current) {
									const isAddLocationRoute = current.endsWith('/locations/add');
									if (navigationState.shouldPrevent && isAddLocationRoute) {
										const text = $i18next.t('general.confirm_leave_page');
										const answer = $window.confirm(text);
										if (!answer) {
											e.preventDefault();
										}
									}
								};
								navigationListener.isLocationChangerListener = true;

								$rootScope.$on('$locationChangeStart', navigationListener);
							}
						}
					}
				]
			]
		}),
		{
			name: 'site.VerifyLocation',
			url: '/locations/verify',
			component: 'pmtVerifyLocationResolver',
			requiredPermission: 'permission/location.edit'
		}
	];
};

module.exports = {
	locationStates,
	locationDetailsStates,
	locationInsightStates
};
